import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { Video } from '@sorare/core/src/atoms/ui/Video';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { glossary } from '@sorare/core/src/lib/glossary';

import { RigidSquareTitle } from 'components/landing/ui/RigidSquareTitle';

import av1Mobile from './assets/output-mobile.av1.webm';
import h264Mobile from './assets/output-mobile.h264.mp4';
import vp9Mobile from './assets/output-mobile.vp9.webm';
import av1 from './assets/output.av1.webm';
import h264 from './assets/output.h264.mp4';
import vp9 from './assets/output.vp9.webm';

const Root = styled.section`
  width: 100%;
  height: 90vh;
  display: grid;
  align-items: center;
  justify-items: center;
  isolation: isolate;
  position: relative;
  &::before {
    position: absolute;
    inset: 0;
    z-index: 1;
    content: '';
    background: linear-gradient(180deg, rgba(16, 16, 16, 0) 75%, #101010 100%),
      radial-gradient(
        50% 50% at 50% 50%,
        rgba(0, 0, 0, 0.6) 0%,
        rgba(0, 0, 0, 0) 100%
      ),
      linear-gradient(180deg, #101010 0%, rgba(16, 16, 16, 0) 17%);
  }
  > * {
    grid-row-start: 1;
    grid-column-start: 1;
    overflow: hidden;
  }
`;
const Header = styled(Vertical)`
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  text-align: center;
  width: 100%;
  transition: 0.3s ease opacity;
  &.unmuted:not(:hover) {
    opacity: 0.1;
  }
`;
const Title = styled.h1`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: var(--fs-landing-title);
  font-weight: normal;
`;
const Buttons = styled(Horizontal)`
  flex-wrap: wrap;
  justify-content: center;
`;

export const HeroVideo = () => {
  const [unmuted, setUnmuted] = useState(false);
  const { signUp } = useConnectionContext();
  return (
    <div>
      <Root>
        <Video
          sources={[
            {
              src: av1Mobile,
              type: 'video/webm; codecs="av01.0.31M.08,opus"',
              media: '(orientation: portrait)',
            },
            {
              src: vp9Mobile,
              type: 'video/webm; codecs="vp9"',
              media: '(orientation: portrait)',
            },
            { src: h264Mobile, media: '(orientation: portrait)' },
            { src: av1, type: 'video/webm; codecs="av01.0.31M.08,opus"' },
            { src: vp9, type: 'video/webm; codecs="vp9"' },
            { src: h264 },
          ]}
          autoPlay
          playsInline
          loop
          muted
          onMuteClick={muted => {
            setUnmuted(muted);
          }}
        />
        <Header center className={classNames({ unmuted })}>
          <Title>
            <FormattedMessage
              id="landing.kylianmbappe.HeroVideo.title"
              defaultMessage="Join Kylian Mbappé's<bold>Competition</bold>"
              values={{
                bold: c => <RigidSquareTitle as="span">{c}</RigidSquareTitle>,
              }}
            />
          </Title>
          <BodyL>
            <FormattedMessage
              id="landing.kylianmbappe.HeroVideo.description"
              defaultMessage="You're just 400 fantasy football points away from meeting Kylian Mbappé.*"
              values={{ br: <br /> }}
            />
          </BodyL>
          <Buttons>
            <Button color="secondary" size="medium" onClick={signUp}>
              <FormattedMessage {...glossary.playNow} />
            </Button>
            <Button
              color="opa"
              size="medium"
              to="/blog/kylian-mbappe-fc-is-looking-for-a-new-manager"
            >
              <FormattedMessage
                id="landing.kylianmbappe.HeroVideo.cta"
                defaultMessage="Learn more"
              />
            </Button>
          </Buttons>
        </Header>
      </Root>
    </div>
  );
};
